<script setup>
import { apiInstance } from "@/utils/axios"
import { computed, onMounted, ref } from "vue"

definePage({
  meta: {
    // keep your layout config elsewhere if needed
  },
})

// ---------------------------------------
// State
// ---------------------------------------
const loading = ref(false)

const masterMe = ref(null)

const stats = ref({
  total: 0,
  pending: 0,
  approved: 0, // UI label "Approved" maps to backend "confirmed"
  rejected: 0,
})

const activityRows = ref([])
const availability = ref(null)
const todaySlots = ref([])

// Optional: if you want to show an error banner/snackbar
const pageError = ref("")

const todayISO = computed(() => {
  const d = new Date()
  const yyyy = d.getFullYear()
  const mm = String(d.getMonth() + 1).padStart(2, "0")
  const dd = String(d.getDate()).padStart(2, "0")
  return `${yyyy}-${mm}-${dd}`
})

// ---------------------------------------
// Auth header (MASTER first)
// ---------------------------------------
const authHeader = computed(() => {
  // Put your master token key FIRST.
  // From our earlier master store rewrite:
  // MASTER_ACCESS_TOKEN is stored under "MASTER_ACCESS_TOKEN".
  const token =
    localStorage.getItem("MASTER_ACCESS_TOKEN") ||
    localStorage.getItem("ACCESS_TOKEN") ||
    localStorage.getItem("USER_ACCESS_TOKEN") ||
    localStorage.getItem("CONTRACTOR_ACCESS_TOKEN")

  return token ? { Authorization: `Bearer ${token}` } : {}
})

// ---------------------------------------
// Helpers
// ---------------------------------------
const safeGet = async (url, params = {}) => {
  // IMPORTANT: master endpoints are under /api
  const res = await apiInstance.get(url, {
    params,
    headers: authHeader.value,
  })
  return res?.data
}

const normalizePaginator = payload => {
  if (Array.isArray(payload)) return { data: payload, total: payload.length }

  return {
    data: Array.isArray(payload?.data) ? payload.data : [],
    total: Number(payload?.total ?? 0),
  }
}

const buildLast7Days = () => {
  const rows = []
  const now = new Date()

  for (let i = 6; i >= 0; i--) {
    const d = new Date(now)
    d.setDate(now.getDate() - i)

    const dd = String(d.getDate()).padStart(2, "0")
    const mm = String(d.getMonth() + 1).padStart(2, "0")
    const label = `${dd}/${mm}`

    rows.push({
      label,
      total: 0,
      pending: 0,
      approved: 0,
      rejected: 0,
    })
  }

  return rows
}

// ---------------------------------------
// Fetchers (ALL use /api/master/...)
// ---------------------------------------
const fetchMe = async () => {
  masterMe.value = await safeGet("/api/master/me")
}

const fetchMeetingCounts = async () => {
  const totalRes = normalizePaginator(
    await safeGet("/api/master/meeting/requests", { per_page: 1 })
  )

  const pendingRes = normalizePaginator(
    await safeGet("/api/master/meeting/requests", { status: "pending", per_page: 1 })
  )

  // UI "Approved" = backend "confirmed"
  const approvedRes = normalizePaginator(
    await safeGet("/api/master/meeting/requests", { status: "confirmed", per_page: 1 })
  )

  const rejectedRes = normalizePaginator(
    await safeGet("/api/master/meeting/requests", { status: "rejected", per_page: 1 })
  )

  stats.value = {
    total: totalRes.total,
    pending: pendingRes.total,
    approved: approvedRes.total,
    rejected: rejectedRes.total,
  }
}

const fetchAvailability = async () => {
  availability.value = await safeGet("/api/master/meeting/availability-settings")
}

const fetchTodaySlots = async () => {
  const res = await safeGet("/api/master/meeting/available-slots", { date: todayISO.value })
  todaySlots.value = Array.isArray(res?.slots) ? res.slots : []
}

const fetchActivityOverview = async () => {
  const rows = buildLast7Days()

  const listRes = normalizePaginator(
    await safeGet("/api/master/meeting/requests", { per_page: 100 })
  )

  const items = listRes.data

  items.forEach(r => {
    const created = r?.created_at ? new Date(r.created_at) : null
    if (!created || Number.isNaN(created.getTime())) return

    const dd = String(created.getDate()).padStart(2, "0")
    const mm = String(created.getMonth() + 1).padStart(2, "0")
    const label = `${dd}/${mm}`

    const row = rows.find(x => x.label === label)
    if (!row) return

    row.total += 1

    const st = r?.status
    if (st === "pending") row.pending += 1
    if (st === "confirmed") row.approved += 1
    if (st === "rejected") row.rejected += 1
  })

  activityRows.value = rows
}

const fetchAll = async () => {
  loading.value = true
  pageError.value = ""

  try {
    await fetchMe()
    await fetchMeetingCounts()
    await fetchAvailability()
    await fetchTodaySlots()
    await fetchActivityOverview()
  } catch (err) {
    // Don't crash the page; show a readable error
    pageError.value =
      err?.response?.data?.message ||
      err?.response?.data?.error ||
      err?.message ||
      "Failed to load dashboard data."
  } finally {
    loading.value = false
  }
}

onMounted(fetchAll)
</script>

<template>
  <div>
    <VAlert v-if="pageError" type="error" variant="tonal" class="mb-4">
      {{ pageError }}
    </VAlert>

    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex flex-wrap align-center justify-space-between gap-2">
          <div>
            <h2 class="text-h4 mb-1">Development MARBLE ADMIN</h2>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Master Dashboard — meeting requests & availability
            </p>
          </div>

          <VBtn color="primary" variant="tonal" :loading="loading" @click="fetchAll">
            Refresh
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Stats cards -->
    <VRow class="match-height">
      <VCol cols="12" md="3">
        <VCard>
          <VCardText>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h6 mb-1">Total</div>
                <div class="text-h4">{{ stats.total }}</div>
                <div class="text-body-2 text-medium-emphasis">Total requests</div>
              </div>
              <VAvatar color="success" variant="tonal" rounded="lg">
                <VIcon icon="tabler-chart-bar" />
              </VAvatar>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="3">
        <VCard>
          <VCardText>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h6 mb-1">Pending</div>
                <div class="text-h4">{{ stats.pending }}</div>
                <div class="text-body-2 text-medium-emphasis">Total pending</div>
              </div>
              <VAvatar color="warning" variant="tonal" rounded="lg">
                <VIcon icon="tabler-hourglass" />
              </VAvatar>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="3">
        <VCard>
          <VCardText>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h6 mb-1">Approved</div>
                <div class="text-h4">{{ stats.approved }}</div>
                <div class="text-body-2 text-medium-emphasis">Total approved</div>
              </div>
              <VAvatar color="success" variant="tonal" rounded="lg">
                <VIcon icon="tabler-circle-check" />
              </VAvatar>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <VCol cols="12" md="3">
        <VCard>
          <VCardText>
            <div class="d-flex align-center justify-space-between">
              <div>
                <div class="text-h6 mb-1">Rejected</div>
                <div class="text-h4">{{ stats.rejected }}</div>
                <div class="text-body-2 text-medium-emphasis">Total rejected</div>
              </div>
              <VAvatar color="error" variant="tonal" rounded="lg">
                <VIcon icon="tabler-circle-x" />
              </VAvatar>
            </div>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <VRow class="match-height mt-2">
      <!-- Activity overview -->
      <VCol cols="12" md="8">
        <VCard>
          <VCardText>
            <div class="d-flex align-center justify-space-between mb-3">
              <div>
                <div class="text-h6">Activity Overview</div>
                <div class="text-body-2 text-medium-emphasis">Last 7 days meeting activity</div>
              </div>
            </div>

            <VTable density="compact">
              <thead>
                <tr>
                  <th>Date</th>
                  <th class="text-center">Total</th>
                  <th class="text-center">Pending</th>
                  <th class="text-center">Approved</th>
                  <th class="text-center">Rejected</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="r in activityRows" :key="r.label">
                  <td>{{ r.label }}</td>
                  <td class="text-center">{{ r.total }}</td>
                  <td class="text-center">{{ r.pending }}</td>
                  <td class="text-center">{{ r.approved }}</td>
                  <td class="text-center">{{ r.rejected }}</td>
                </tr>
              </tbody>
            </VTable>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Availability -->
      <VCol cols="12" md="4">
        <VCard>
          <VCardText>
            <div class="text-h6 mb-2">Availability</div>
            <div class="text-body-2 text-medium-emphasis mb-4">Current settings</div>

            <div class="mb-3">
              <div class="text-caption text-medium-emphasis">Timezone</div>
              <div class="text-body-1">
                {{ availability?.time_zone || "N/A" }}
              </div>
            </div>

            <div class="mb-4">
              <div class="text-caption text-medium-emphasis">Working Hours</div>
              <div class="text-body-1">
                {{ availability?.opening_time || "N/A" }} — {{ availability?.closing_time || "N/A" }}
              </div>
            </div>

            <VBtn to="/master/meetings/availability" color="primary" variant="tonal" block>
              Manage Availability
            </VBtn>

            <VDivider class="my-4" />

            <div class="text-h6 mb-2">Today’s Slots</div>

            <div v-if="todaySlots.length" class="d-flex flex-wrap gap-2">
              <VChip v-for="(s, i) in todaySlots.slice(0, 10)" :key="i" label>
                {{ typeof s === "string" ? s : (s?.label || s?.time || "Slot") }}
              </VChip>

              <VChip v-if="todaySlots.length > 10" label color="primary" variant="tonal">
                +{{ todaySlots.length - 10 }} more
              </VChip>
            </div>

            <div v-else class="text-medium-emphasis">
              No available slots today (or none returned).
            </div>

            <VBtn to="/master/meetings/slots" color="primary" variant="tonal" block class="mt-4">
              View Slots
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>
  </div>
</template>

<style scoped>
.match-height {
  align-items: stretch;
}
</style>
